import type { JSX } from 'react';
import { Modal } from 'antd';
import { BellOutlined, CheckCircleOutlined, DownloadOutlined, EditOutlined, MessageOutlined, SendOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import DOMPurify from 'dompurify';

import { Button, Tag, exportCsv } from '../ds';
import { ReactionBar } from '../ReactionBar';
import { api } from '../../lib/api';
import type { NewsItemDto } from '../../lib/wikiApi';
import { NewsAttachments } from './NewsAttachments';
import { NewsPoll } from './NewsPoll';
import { isImportant } from './news-utils';

const CRM_NEWS_URL = 'https://crm.brasol.ru/news';

type AckRow = { partnerId: string; partnerName: string; userName: string; ackedAt: string };

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Просмотр публикации HUB 2.0 (порт CRM frontend/src/pages/news/NewsViewerModal):
 * категория и приоритет, «Требует действия», тело, опрос, вложения, реакции,
 * каналы. Для УК (canManage) — охват: кто подтвердил (CRM /news/:id/ack-list,
 * право news:edit_any по общей cookie; без права блок не показывается) +
 * «Список в CSV» и «Редактировать в CRM». Писать новости из HUB нельзя —
 * только через CRM.
 */
export function NewsViewerModal({ item, onClose, canManage = false }: { item: NewsItemDto | null; onClose: () => void; canManage?: boolean }): JSX.Element {
  const acks = useQuery({
    queryKey: ['wiki', 'news', 'ack-list', item?.id],
    queryFn: () => api.get<AckRow[]>(`/news/${encodeURIComponent(item!.id)}/ack-list`),
    enabled: !!item && canManage,
    retry: false,
  });
  const requiresAction = item?.priority === 'REQUIRES_ACTION';

  return (
    <Modal
      open={!!item}
      title={null}
      onCancel={onClose}
      width={720}
      destroyOnHidden
      footer={
        item ? (
          <span style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button variant="ghost" onClick={onClose}>
              Закрыть
            </Button>
            {canManage && (
              <Button variant="indigo" icon={<EditOutlined />} href={`${CRM_NEWS_URL}/${encodeURIComponent(item.id)}`}>
                Редактировать в CRM
              </Button>
            )}
          </span>
        ) : null
      }
    >
      {item && (
        <>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            {item.category?.name && <Tag color="blue">{item.category.name}</Tag>}
            {requiresAction ? <Tag color="red">Требует действия</Tag> : isImportant({ isPinned: !!item.isPinned, priority: item.priority ?? 'NORMAL' }) ? <Tag color="orange">Важное</Tag> : null}
            <span style={{ fontSize: 13, color: 'var(--fk-text-muted)', marginLeft: 'auto' }}>{fmtDate(item.publishedAt ?? item.createdAt)}</span>
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 12px', color: 'var(--fk-indigo)', lineHeight: 1.25 }}>{item.title}</h2>
          {requiresAction && (
            <div className="fk-adm-callout" style={{ marginTop: 0, marginBottom: 14, display: 'flex', gap: 8 }}>
              <CheckCircleOutlined style={{ color: 'var(--fk-orange)', marginTop: 3 }} />
              <span>
                <b>Требует действия.</b> Открытие публикации засчитывается как ознакомление{item.poll ? ', голос в опросе — тоже' : ''}.
              </span>
            </div>
          )}
          <div className="wiki-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.bodyHtml || item.excerpt || '') }} />
          {item.poll && <NewsPoll key={item.poll.id} newsId={item.id} poll={item.poll} />}
          {item.attachments && item.attachments.length > 0 && <NewsAttachments newsId={item.id} attachments={item.attachments} />}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
            <ReactionBar targetType="news" targetId={item.id} />
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>Опубликовано в:</span>
            <Tag color="indigo" icon={<BellOutlined />}>HUB и CRM</Tag>
            {item.broadcastTelegram && <Tag color="blue" icon={<SendOutlined />}>Telegram</Tag>}
            {item.broadcastMax && <Tag color="indigo" icon={<MessageOutlined />}>MAX</Tag>}
          </div>
          {canManage && acks.data && (
            <div style={{ marginTop: 18, padding: 14, borderRadius: 8, border: '1px solid var(--fk-border)', background: 'var(--fk-bg-subtle)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <b style={{ fontSize: 14 }}>Ознакомились · {acks.data.length}</b>
                <span style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>партнёров подтвердили</span>
                <span style={{ flex: 1 }} />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<DownloadOutlined />}
                  disabled={!acks.data.length}
                  onClick={() =>
                    exportCsv({
                      filename: `Ознакомление — ${item.title.slice(0, 60)}`,
                      columns: [
                        { title: 'Партнёр', value: (r: AckRow) => r.partnerName },
                        { title: 'Кто', value: (r: AckRow) => r.userName },
                        { title: 'Когда', value: (r: AckRow) => new Date(r.ackedAt).toLocaleString('ru-RU') },
                      ],
                      rows: acks.data ?? [],
                    })
                  }
                >
                  Список в CSV
                </Button>
              </div>
              {acks.data.length > 0 && (
                <div style={{ marginTop: 8, maxHeight: 160, overflowY: 'auto', fontSize: 13 }}>
                  {acks.data.slice(0, 50).map((r) => (
                    <div key={`${r.partnerId}:${r.ackedAt}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '4px 0', borderTop: '1px solid var(--fk-row-line)' }}>
                      <span>
                        {r.partnerName} <span style={{ color: 'var(--fk-text-muted)' }}>· {r.userName}</span>
                      </span>
                      <span style={{ color: 'var(--fk-text-muted)', whiteSpace: 'nowrap' }}>{new Date(r.ackedAt).toLocaleDateString('ru-RU')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
